/**
 * Визард «Добавить поля из примера Jira» — для существующего ItemType.
 *
 * Использует тот же UI, что и `CreateItemTypeFromJiraWizard` (InputStep +
 * EditStep), но другие endpoint'ы:
 *   - preview: `/preview-add-fields` отдаёт draft, в котором поля,
 *     дублирующие существующие slug'и, помечены skip+warning.
 *   - commit: `/commit-add-fields` создаёт только новые поля и мерджит их в
 *     существующий JiraSyncConfig (или создаёт новый, если его не было).
 */
import { useCallback, useState } from 'react';
import { Alert, Button, Modal, Space, message } from 'antd';
import type { ItemTypeFromJiraSampleDraft } from '@lens/shared';
import {
  commitAddFieldsFromJira,
  previewAddFieldsFromJira,
} from '../jira/api.ts';
import {
  EditStep,
  InputStep,
} from './CreateItemTypeFromJiraWizard.tsx';

interface Props {
  spaceId: string;
  itemTypeId: string;
  itemTypeName: string;
  open: boolean;
  onCancel: () => void;
  onAdded: (createdFieldIds: string[]) => void;
}

export function AddFieldsFromJiraWizard({
  spaceId,
  itemTypeId,
  itemTypeName,
  open,
  onCancel,
  onAdded,
}: Props): JSX.Element {
  const [step, setStep] = useState<'input' | 'edit'>('input');
  const [issueKeysText, setIssueKeysText] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<ItemTypeFromJiraSampleDraft | null>(null);
  const [lockedSlugs, setLockedSlugs] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('input');
    setIssueKeysText('');
    setDraft(null);
    setError(null);
    setLoading(false);
  }, []);

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  const handlePreview = useCallback(async () => {
    const keys = issueKeysText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      setError('Введите хотя бы один ключ задачи Jira');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await previewAddFieldsFromJira(spaceId, itemTypeId, keys);
      setDraft(result.draft);
      setLockedSlugs(new Set(result.existingFieldSlugs));
      setStep('edit');
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось получить превью');
    } finally {
      setLoading(false);
    }
  }, [issueKeysText, spaceId, itemTypeId]);

  const handleCommit = useCallback(async () => {
    if (!draft) return;
    setError(null);
    setLoading(true);
    try {
      const result = await commitAddFieldsFromJira(spaceId, itemTypeId, draft);
      message.success(`Добавлено полей: ${result.createdFieldIds.length}`);
      onAdded(result.createdFieldIds);
      reset();
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось добавить поля');
    } finally {
      setLoading(false);
    }
  }, [draft, itemTypeId, onAdded, reset, spaceId]);

  const updateField = useCallback(
    (jiraField: string, patch: any) => {
      setDraft((prev) =>
        prev
          ? { ...prev, fields: prev.fields.map((f) => (f.jiraField === jiraField ? { ...f, ...patch } : f)) }
          : prev,
      );
    },
    [],
  );

  const updateDict = useCallback(
    (refKey: string, patch: any) => {
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              proposedDictionaries: prev.proposedDictionaries.map((d) =>
                d.refKey === refKey ? { ...d, ...patch } : d,
              ),
            }
          : prev,
      );
    },
    [],
  );

  // toggleTitle не нужен — у типа уже есть заголовок, в add-fields draft
  // isTitle всегда false. Ставим no-op.
  const toggleTitle = useCallback(() => {}, []);

  return (
    <Modal
      open={open}
      title={`Добавить поля из Jira — ${itemTypeName}`}
      width="92vw"
      style={{ top: 24, maxWidth: 1400 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      onCancel={handleCancel}
      footer={
        step === 'input' ? (
          <Space>
            <Button onClick={handleCancel}>Отмена</Button>
            <Button type="primary" loading={loading} onClick={handlePreview}>
              Получить превью
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => { setStep('input'); setError(null); }}>Назад</Button>
            <Button onClick={handleCancel}>Отмена</Button>
            <Button type="primary" loading={loading} onClick={handleCommit}>
              Добавить
            </Button>
          </Space>
        )
      }
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {step === 'input' ? (
        <InputStep value={issueKeysText} onChange={setIssueKeysText} loading={loading} />
      ) : draft ? (
        <EditStep
          draft={draft}
          setDraft={setDraft}
          updateField={updateField}
          updateDict={updateDict}
          toggleTitle={toggleTitle}
          lockedSlugs={lockedSlugs}
          hideTitleColumn
        />
      ) : null}
    </Modal>
  );
}
